import { yupResolver } from '@hookform/resolvers/yup';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import { Button, Dropdown, Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiChevronDown, BiSolidBot, BiSolidChat, BiSolidMessageDetail } from 'react-icons/bi';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { useGetAllScenariosQuery } from '@/app/services/chatbot-rtk.services';
import { BiLouise } from '@/features/appearance/AppearanceSharedAi';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import useComponentVisible from '../../../../../hooks/useComponentVisible';
import AddNoteButton from '../../AddNoteButton';
import DeleteButton from '../../DeleteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import { MacBotButtonSettingForm } from './scenario-button-setting-form.component';
import ScenarioSide from './scenario-side';
import { ScenarioSimpleTextInput } from './scenario-simple-text-input';

export const MacBotASKToAIForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  config,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow?: (item: boolean) => void;
  itemData: any;
  parentId: string | null;
  tree: any[];
  config: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
}) => {
  const { t } = useTranslation();
  const [showButtonSetting, setShowButtonSetting] = useState(false);
  const [selectedButtonToEdit, setSelectedButtonToEdit] = useState<number>(-1);
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
    currentCaretPosition: 0,
  });

  const { ref, isComponentVisible } = useComponentVisible(showContextMenu);
  const [cloneJson, setCloneJson] = useState<IResponseForm | null>(null);
  const userId = useAppSelector(selectUseId);
  const currentScenarioId = useAppSelector(state => state.bots.wizard.id);

  const { isLoading, isError, isSuccess, refetch, data } = useGetAllScenariosQuery({ botType: 'widget' });

  interface IResponseForm extends INoteForm {
    aiBotId: string;
    aiBotName: string;
    additionalInfo?: string;
    showResult: boolean;
  }

  const schema = Yup.object({
    aiBotName: Yup.string().required(t('PassToAI/key-07')),
    aiBotId: Yup.string().required(t('PassToAI/key-06')),
    additionalInfo: Yup.string().required(t('PassToAI/key-05')),
    showResult: Yup.boolean().default(false).required(),
    ...getNoteSchema(t),
  });

  const types: { [key: string]: string } = {
    MCBOT_01: t('DashboardCreatePartial/key3'),
    KITTY_02: t('DashboardCreatePartial/key4'),
    KITTY_03: t('DashboardCreatePartial/key2'),
    CLONE_01: t('DashboardCreatePartial/key1'),
    KITTY_01: t('DashboardCreatePartial/key9'),
    VIDEO_SCENARIO: t('DashboardCreatePartial/key5'),
    KITTY_04: t('DashboardCreatePartial/key6'),
  };

  const supportBots = [BotTypeEnum.AI_READY_TO_USE, BotTypeEnum.AI_WITH_KNOWLEDGE_BASE];

  const {
    register,
    formState: { errors },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control,
  } = useForm<IResponseForm>({
    resolver: yupResolver(schema) as any,
    mode: 'onSubmit',
    defaultValues: {
      showResult: false,
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });
  //Check Show Button Delete
  useEffect(() => {
    if (itemData) {
      const find = tree?.find(dr => dr.id == itemData.id);
      if (find) {
        if (find.parent) {
          setShowDeleteButton(true);
        } else {
          setShowDeleteButton(false);
        }
      }
    }
  }, [tree, itemData]);

  const [contextMenuList, setContextMenuList] = useState<any[]>([]);
  const popupAudioPath: { [key: string]: string } = {
    en: 'https://cdn.1cdn.app/application/LOUISEBOT/24092716242734_add_bot_message_en.mp3',
    fr: 'https://cdn.1cdn.app/application/LOUISEBOT/24092716242877_add_bot_message_fr.mp3',
  };

  useEffect(() => {
    reset({
      aiBotId: itemData?.aiBotId ?? '',
      aiBotName: itemData?.aiBotName ?? '',
      notes: itemData?.notes,
      additionalInfo: itemData?.additionalInfo ?? '',
      showNoteForm: itemData?.notes?.length > 0 || false,
      showResult: itemData?.showResult || false,
    });

    setCloneJson({
      aiBotId: itemData?.aiBotId ?? '',
      aiBotName: itemData?.aiBotName ?? '',
      notes: itemData?.notes,
      additionalInfo: itemData?.additionalInfo ?? '',
      showNoteForm: itemData?.notes?.length > 0 || false,
      showResult: itemData?.showResult || false,
    });

    if (!itemData) {
      setShowDeleteButton(false);
    }
  }, [itemData]);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
  };

  const removeButtonHandler = (btnIndex: number) => {
    setQuickReplies(prev => prev.filter((p, index) => index !== btnIndex));
  };
  const addButtonHandler = (qBtn: number) => {
    setQuickReplies(prev => [...prev, qBtn]);
  };

  const editButtonHandler = (_index: number, qRep: any) => {
    setQuickReplies(prev => prev.map((p, index) => (index === _index ? qRep : p)));

    setSelectedButtonToEdit(-1);
  };

  const handleQuickReplySelect = (index: number) => {
    setSelectedButtonToEdit(index);
    setShowButtonSetting(true);
  };

  const closeButtonSettingModal = () => {
    setShowButtonSetting(false);
    setSelectedButtonToEdit(-1);
  };

  const ButtonSubmit = (info: IResponseForm) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.AskToAI,
        additionalInfo: info.additionalInfo,
        aiBotId: info.aiBotId,
        parent: itemData.parent,
        title: info.aiBotName,
        aiBotName: info.aiBotName,
        notes: info.notes,
        showResult: info.showResult,
      };

      if (
        JSON.stringify(cloneJson) ==
        JSON.stringify({
          additionalInfo: info.additionalInfo,
          BotResponaiBotIdse: info.aiBotId,
          aiBotName: info.aiBotName,
          notes: info.notes,
          showResult: info.showResult,
        })
      ) {
        setShow?.(false);
        return;
      }

      changeScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    } else {
      const _data = {
        type: BlockType.AskToAI,
        additionalInfo: info.additionalInfo,
        aiBotId: info.aiBotId,
        parent: parentId,
        aiBotName: info.aiBotName,
        notes: info.notes,
        title: info.aiBotName,
        showResult: info.showResult,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    }
  };

  const renderVideoFloatingButton = (icon?: { type: string; value: string }) => {
    const icons: Record<string, React.ReactNode> = {
      '4': <BiLouise />,
      '1': <BiSolidBot size={30} />,
      '2': <BiSolidChat size={30} />,
      '3': <BiSolidMessageDetail size={30} />,
    };

    if (icon?.type === 'icon')
      return (
        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-blue-500 tw-text-white">
          {icons[icon.value]}
        </div>
      );
    if (icon?.type === 'image') return <img src={icon.value} alt={''} className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover" />;
    if (icon?.type === 'video')
      return (
        <video autoPlay loop muted playsInline className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover">
          <source src={icon.value}></source>
        </video>
      );

    return null;
  };

  const handleRightClick = (event: any) => {
    event.preventDefault();
    setShowContextMenu(true);
    const rect = event.currentTarget.getBoundingClientRect();
    const currentCaretPosition = event.currentTarget.selectionStart;

    setContextMenuPosition({
      top: event.clientY - rect.top,
      left: event.clientX - rect.left,
      currentCaretPosition: currentCaretPosition,
    });
  };

  // Function to measure caret's pixel position using a canvas approach
  const measureCaret = (input: any, caretIndex: any) => {
    const rect = input.getBoundingClientRect();
    return { top: rect.top, left: rect.left };
  };

  useEffect(() => {
    setShowContextMenu(false);
  }, [isComponentVisible]);

  useEffect(() => {
    if (config?.metaData?.variables) {
      setContextMenuList([...config.metaData.variables]);
    }
  }, [config]);

  const CustomToggle = React.forwardRef(
    ({ children, onClick }: { children: React.ReactNode; onClick: MouseEventHandler }, ref: React.LegacyRef<HTMLButtonElement>) => (
      <button
        id="btn-custom-toggle"
        className="tw-border-none tw-outline-none tw-ring-0 tw-bg-transparent tw-p-0 tw-m-0 tw- tw-h-[58px] tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex"
        ref={ref}
        onClick={e => {
          e.preventDefault();
          onClick(e);
        }}
        type="button"
      >
        <div className="tw-self-stretch tw-h-[58px] tw-flex-col tw-justify-start tw-items-start tw-flex">
          <div className="tw-self-stretch tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex">
            <div className="tw-grow tw-shrink tw-basis-0 tw-h-[58px] tw-pl-[17px] tw-pr-3 tw-py-[9px] tw-justify-start tw-items-center tw-gap-2 tw-flex">
              <div className="tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-inline-flex">{children}</div>
            </div>
            <div className="tw-px-[13px] tw-py-[9px] tw-bg-white tw-justify-start tw-items-center tw-gap-1 tw-flex">
              <BiChevronDown size={24} />
            </div>
          </div>
        </div>
      </button>
    )
  );
  CustomToggle.displayName = 'CustomToggle';

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowAskToAI}
          onClose={() => {
            reset();
          }}
        />

        <div className="tw-w-full tw-h-full tw-px-3 tw-py-2 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex tw-font-[Lexend]">
          <Form.Group className="tw-w-full ">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('PassToAI/key-02')}</Form.Label>

            <ScenarioSimpleTextInput
              isMultiline={true}
              placeholder={t('PassToAI/key-04')}
              setValue={text => {
                setValue('additionalInfo', text?.trim());
              }}
              value={getValues('additionalInfo') as string}
              contextMenuList={contextMenuList}
              isInvalid={!!errors.additionalInfo}
            />
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors.additionalInfo?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="tw-w-full">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('AskToAI/key-01')}</Form.Label>
            <Controller
              name="aiBotId"
              control={control}
              render={({ field }) => (
                <React.Fragment>
                  {isError ? (
                    <Button id="btn-retry" type="button" onClick={refetch}>
                      Retry
                    </Button>
                  ) : (
                    <Dropdown translate="no" className="tw-w-full">
                      <Dropdown.Toggle as={CustomToggle}>
                        {field.value ? (
                          <div className="tw-flex tw-items-center tw-gap-2">
                            {data && data?.find(item => item.id === field.value) ? (
                              <div className="tw-relative">
                                {data?.find(item => item.id === field.value)?.type === BotTypeEnum.VIDEO_SCENARIO ? (
                                  renderVideoFloatingButton(data?.find(item => item.id === field.value)?.icon)
                                ) : (
                                  <img
                                    className="tw-w-10 tw-h-10 tw-rounded-[20px] tw-object-cover"
                                    src={
                                      data?.find(item => item.id === field.value)?.logo && data?.find(item => item.id === field.value)?.logo != ''
                                        ? data?.find(item => item.id === field.value)?.logo
                                        : 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png'
                                    }
                                    alt=""
                                  />
                                )}

                                <OverlayTrigger
                                  overlay={
                                    <Tooltip>
                                      {t('General/key3')}{' '}
                                      {!data?.find(item => item.id === field.value)?.is_default
                                        ? t('DashboardHeaderSection/deactivated')
                                        : t('DashboardHeaderSection/activated')}
                                    </Tooltip>
                                  }
                                >
                                  <div
                                    className={`tw-w-4 tw-h-4 tw-absolute tw-right-0 tw-bottom-0 ${
                                      !data?.find(item => item.id === field.value)?.is_default ? 'tw-bg-red-400' : 'tw-bg-emerald-400 blink'
                                    } tw-rounded-full  tw-shadow tw-border-2 tw-border-solid tw-border-white`}
                                  />
                                </OverlayTrigger>
                              </div>
                            ) : null}
                            <div className="tw-text-left">
                              <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                                {data?.find(item => item.id === field.value)?.title}
                              </div>
                              <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                                {types[data?.find(item => item.id === field.value)?.type as string]}
                              </div>
                            </div>
                          </div>
                        ) : (
                          t('Wizard/CloneExtraVideo/key3')
                        )}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="tw-w-full">
                        {isLoading ? (
                          <Dropdown.Item>Loading...</Dropdown.Item>
                        ) : isSuccess &&
                          data?.filter(dr => supportBots.includes(dr.type as BotTypeEnum) && dr.id != currentScenarioId && dr.is_default == true)?.length ===
                            0 ? (
                          <Dropdown.Item>{t('Wizard/CloneExtraVideo/key4')}</Dropdown.Item>
                        ) : (
                          <React.Fragment>
                            {data
                              ?.filter(dr => supportBots.includes(dr.type as BotTypeEnum) && dr.id != currentScenarioId && dr.is_default == true)
                              ?.map((opt, index) => (
                                <Dropdown.Item
                                  key={index}
                                  onClick={() => {
                                    setValue('aiBotId', opt.id);
                                    setValue('aiBotName', opt.title);
                                  }}
                                >
                                  <div className="tw-flex tw-items-center tw-gap-2">
                                    <div className="tw-relative">
                                      {opt.type === BotTypeEnum.VIDEO_SCENARIO ? (
                                        renderVideoFloatingButton(opt.icon)
                                      ) : (
                                        <img
                                          className="tw-w-10 tw-h-10 tw-rounded-[20px] tw-object-cover"
                                          src={
                                            opt.logo && opt.logo != '' ? opt.logo : 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png'
                                          }
                                          alt=""
                                        />
                                      )}

                                      <OverlayTrigger
                                        overlay={
                                          <Tooltip>
                                            {t('General/key3')}{' '}
                                            {!opt.is_default ? t('DashboardHeaderSection/deactivated') : t('DashboardHeaderSection/activated')}
                                          </Tooltip>
                                        }
                                      >
                                        <div
                                          className={`tw-w-4 tw-h-4 tw-absolute tw-right-0 tw-bottom-0 ${
                                            !opt.is_default ? 'tw-bg-red-400' : 'tw-bg-emerald-400 blink'
                                          } tw-rounded-full  tw-shadow tw-border-2 tw-border-solid tw-border-white`}
                                        />
                                      </OverlayTrigger>
                                    </div>
                                    <div className="tw-text-left">
                                      <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{opt.title}</div>
                                      <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{types[opt.type]}</div>
                                    </div>
                                  </div>
                                </Dropdown.Item>
                              ))}
                          </React.Fragment>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </React.Fragment>
              )}
            />
            <Form.Control.Feedback type="invalid" className="tw-p-2 d-block font-poppins">
              {errors.aiBotId?.message}
            </Form.Control.Feedback>
          </div>

          <div className="tw-flex-col tw-flex tw-justify-between tw-gap-3 tw-w-full">
            <Form.Group className="tw-flex tw-items-center tw-justify-between tw-h-12 tw-pl-4 tw-pr-3 tw-py-3 tw-bg-white tw-rounded-md tw-border tw-border-solid tw-border-gray-300 ">
              <Form.Label htmlFor="" className="tw-text-[#11181c] tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-0">
                {t('PassToAI/key-08')}
              </Form.Label>

              <Form.Switch {...register(`showResult`)} />
            </Form.Group>
          </div>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {showDeleteButton && <DeleteButton deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}

            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <Button id="btn-submit" type="submit" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : <span>{t('MacBotResponseForm/btn-02')}</span>}
            </Button>
          </div>
        </div>

        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>

      <MacBotButtonSettingForm
        show={showButtonSetting}
        closeModal={closeButtonSettingModal}
        addQuickReplies={addButtonHandler}
        editQuickReplies={editButtonHandler}
        selectedButtonToEdit={selectedButtonToEdit}
        quickReplies={quickReplies}
        removeButton={removeButtonHandler}
        tree={tree}
      />
    </React.Fragment>
  );
};
